import { z } from "zod";

import { SupportedTwilioRegionSchema } from "../twilio-region.types";
import type { TwilioContentTypes } from "./twilio-content-types";

// ============================================================================
// COMPLIANCE BUNDLE (DB entity shape for API response)
// ============================================================================

/**
 * Compliance Bundle Schema — matches the ComplianceBundle Prisma model.
 * Used in API responses instead of the old RegulatoryCompliance JSON blob.
 */
export const ComplianceBundleResponseSchema = z.object({
  id: z.string().uuid(),
  connectionId: z.string().uuid(),
  status: z.string(),
  isoCountry: z.string(),
  numberType: z.string(),
  endUserType: z.string(),
  regulationSid: z.string().nullable(),
  bundleSid: z.string().nullable(),
  endUserSid: z.string().nullable(),
  businessName: z.string().nullable(),
  submittedAt: z.coerce.date().nullable(),
  approvedAt: z.coerce.date().nullable(),
  rejectionReason: z.string().nullable(),
  draftFormData: z.unknown().nullable(),
  draftStep: z.number().int().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type ComplianceBundleResponse = z.infer<typeof ComplianceBundleResponseSchema>;

// ============================================================================
// A2P REGISTRATION (DB entity shape for API response)
// ============================================================================

/**
 * A2P Registration Schema — matches the A2pRegistration Prisma model.
 * Used in API responses instead of the old A2pRegistration JSON blob.
 */
export const A2pRegistrationResponseSchema = z.object({
  id: z.string().uuid(),
  connectionId: z.string().uuid(),
  status: z.string(),
  customerProfileSid: z.string().nullable(),
  trustProductSid: z.string().nullable(),
  customerProfileApproved: z.boolean(),
  trustProductApproved: z.boolean(),
  brandRegistrationSid: z.string().nullable(),
  campaignSid: z.string().nullable(),
  messagingServiceSid: z.string().nullable(),
  submittedAt: z.coerce.date().nullable(),
  approvedAt: z.coerce.date().nullable(),
  rejectionReason: z.string().nullable(),
  draftFormData: z.unknown().nullable(),
  draftStep: z.number().int().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type A2pRegistrationResponse = z.infer<typeof A2pRegistrationResponseSchema>;

// ============================================================================
// TWILIO CONNECTION (Subaccount Management)
// ============================================================================

/**
 * Twilio Connection Schema
 *
 * Represents a Twilio subaccount (ApiKeyConnection with service=TWILIO)
 * Used for WhatsApp/SMS deployments.
 *
 * Compliance and A2P data come from dedicated DB tables (ComplianceBundle,
 * A2pRegistration) instead of the old connection.settings JSON blob.
 */
export const TwilioConnectionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  accountSid: z.string(),
  wabaId: z.string().nullable().optional(),
  region: SupportedTwilioRegionSchema.nullable().default(null),
  organizationId: z.string(),
  status: z.string(),
  complianceBundles: z.array(ComplianceBundleResponseSchema).default([]),
  a2pRegistration: A2pRegistrationResponseSchema.nullable().default(null),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type TwilioConnection = z.infer<typeof TwilioConnectionSchema>;

/**
 * Create Twilio Connection Body Schema
 *
 * Input for creating a new Twilio subaccount.
 * - Region defaults to US1 if not specified
 * - Name is optional for first connection (auto-generated from org name)
 * - Name is required for additional connections (enterprise feature)
 */
export const CreateTwilioConnectionBodySchema = z.object({
  name: z.string().trim().min(1).max(50).optional(),
  region: SupportedTwilioRegionSchema.optional()
});

export type CreateTwilioConnectionBody = z.infer<typeof CreateTwilioConnectionBodySchema>;

/**
 * List Twilio Connections Response Schema
 */
export const ListTwilioConnectionsResponseSchema = z.array(TwilioConnectionSchema);

export type ListTwilioConnectionsResponse = z.infer<typeof ListTwilioConnectionsResponseSchema>;

/**
 * Create Twilio Connection Response Schema
 *
 * Response after creating a new Twilio subaccount
 */
export const CreateTwilioConnectionResponseSchema = TwilioConnectionSchema;

export type CreateTwilioConnectionResponse = z.infer<typeof CreateTwilioConnectionResponseSchema>;

/**
 * Update Twilio Connection Body Schema
 *
 * Supports updating wabaId (WhatsApp Business Account) and region
 */
export const UpdateTwilioConnectionBodySchema = z.object({
  wabaId: z.string().min(1).optional(),
  region: SupportedTwilioRegionSchema.optional()
});

export type UpdateTwilioConnectionBody = z.infer<typeof UpdateTwilioConnectionBodySchema>;

/**
 * Update Twilio Connection Path Vars Schema
 */
export const UpdateTwilioConnectionPathVarsSchema = z.object({
  id: z.string().uuid()
});

export type UpdateTwilioConnectionPathVars = z.infer<typeof UpdateTwilioConnectionPathVarsSchema>;

/**
 * Delete Twilio Connection Path Vars Schema
 */
export const DeleteTwilioConnectionPathVarsSchema = z.object({
  id: z.string().uuid()
});

export type DeleteTwilioConnectionPathVars = z.infer<typeof DeleteTwilioConnectionPathVarsSchema>;

/**
 * Delete Twilio Connection Response Schema
 */
export const DeleteTwilioConnectionResponseSchema = z.object({
  success: z.boolean()
});

export type DeleteTwilioConnectionResponse = z.infer<typeof DeleteTwilioConnectionResponseSchema>;

// ============================================================================
// PHONE NUMBER PURCHASE (WhatsApp-specific)
// ============================================================================

/**
 * Purchase WhatsApp Phone Body Schema
 *
 * Purchase a phone number via a Twilio subaccount.
 * User selects which connection to use (can purchase any country phone from any connection).
 */
export const PurchaseWhatsappPhoneBodySchema = z.object({
  connectionId: z.string().uuid(), // Required: Which Twilio connection to use
  phoneNumber: z.string(),
  countryCode: z.string().length(2), // For metadata (region not restricted by country)
  /** Approved regulatory compliance bundle SID (required for regulated countries) */
  bundleSid: z.string().optional(),
  /** Twilio Address SID for regulated countries */
  addressSid: z.string().optional()
});

export type PurchaseWhatsappPhoneBody = z.infer<typeof PurchaseWhatsappPhoneBodySchema>;

/**
 * Purchase WhatsApp Phone Response Schema
 */
export const PurchaseWhatsappPhoneResponseSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  friendlyName: z.string().nullable(),
  countryCode: z.string()
});

export type PurchaseWhatsappPhoneResponse = z.infer<typeof PurchaseWhatsappPhoneResponseSchema>;

/**
 * List Connection Phone Numbers Path Vars Schema
 */
export const ListConnectionPhoneNumbersPathVarsSchema = z.object({
  connectionId: z.string().uuid()
});

export type ListConnectionPhoneNumbersPathVars = z.infer<
  typeof ListConnectionPhoneNumbersPathVarsSchema
>;

/**
 * Connection Phone Number Schema
 *
 * Phone number with deployment usage info
 */
export const ConnectionPhoneNumberSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  friendlyName: z.string().nullable(),
  countryCode: z.string(),
  deployments: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      type: z.string()
    })
  )
});

export type ConnectionPhoneNumber = z.infer<typeof ConnectionPhoneNumberSchema>;

/**
 * List Connection Phone Numbers Response Schema
 */
export const ListConnectionPhoneNumbersResponseSchema = z.array(ConnectionPhoneNumberSchema);

export type ListConnectionPhoneNumbersResponse = z.infer<
  typeof ListConnectionPhoneNumbersResponseSchema
>;

// ============================================================================
// WHATSAPP SENDER (Meta Profile Management)
// ============================================================================

/**
 * Create WhatsApp Sender Body Schema
 *
 * Creates a WhatsApp sender profile (Meta messaging service) via Twilio
 */
export const CreateWhatsappSenderBodySchema = z.object({
  connectionId: z.string().uuid(),
  phoneNumberId: z.string().uuid(),
  /** Optional — backend reads from connection settings if not provided */
  wabaId: z.string().optional(),
  senderName: z.string().trim().min(1)
});

export type CreateWhatsappSenderBody = z.infer<typeof CreateWhatsappSenderBodySchema>;

/**
 * WhatsApp Sender Offline Reason Schema
 *
 * Explains why a sender is offline (Meta disabled, configuration issue, etc.)
 */
export const WhatsappSenderOfflineReasonSchema = z.object({
  code: z.string(),
  message: z.string(),
  more_info: z.string()
});

/**
 * WhatsApp Sender Schema
 *
 * Represents a WhatsApp sender (phone number registered with Meta).
 * Status indicates readiness: OFFLINE (pending/disabled), ONLINE (ready to send).
 */
export const WhatsappSenderSchema = z.object({
  id: z.string(),
  senderId: z.string(),
  name: z.string(),
  wabaId: z.string(),
  phoneNumberId: z.string().uuid(),
  apiKeyConnectionId: z.string().optional(),
  status: z.enum(["OFFLINE", "ONLINE"]).optional(), // OFFLINE = pending/disabled, ONLINE = ready
  offline_reasons: z.array(WhatsappSenderOfflineReasonSchema).optional(), // Why OFFLINE (Meta disabled, etc.)
  phoneNumber: z
    .object({
      id: z.string().uuid(),
      number: z.string(),
      friendlyName: z.string().nullable(),
      countryCode: z.string()
    })
    .optional()
});

export type WhatsappSender = z.infer<typeof WhatsappSenderSchema>;

/**
 * Create WhatsApp Sender Response Schema
 */
export const CreateWhatsappSenderResponseSchema = WhatsappSenderSchema;

export type CreateWhatsappSenderResponse = z.infer<typeof CreateWhatsappSenderResponseSchema>;

/**
 * Recreate WhatsApp Sender Path Vars Schema
 *
 * Path variables for recreating (delete + create) an OFFLINE sender
 */
export const RecreateWhatsappSenderPathVarsSchema = z.object({
  senderId: z.string()
});

export type RecreateWhatsappSenderPathVars = z.infer<typeof RecreateWhatsappSenderPathVarsSchema>;

/**
 * Recreate WhatsApp Sender Response Schema
 *
 * Returns the newly created sender after delete + recreate
 */
export const RecreateWhatsappSenderResponseSchema = WhatsappSenderSchema;

export type RecreateWhatsappSenderResponse = z.infer<typeof RecreateWhatsappSenderResponseSchema>;

/**
 * Delete WhatsApp Sender — path vars + response.
 * `deregistered` is false when Twilio/Meta rejected the deregistration (removed
 * in Nexus, admin issue filed).
 */
export const DeleteWhatsappSenderPathVarsSchema = z.object({
  senderId: z.string()
});

export type DeleteWhatsappSenderPathVars = z.infer<typeof DeleteWhatsappSenderPathVarsSchema>;

export const DeleteWhatsappSenderResponseSchema = z.object({
  id: z.string(),
  deregistered: z.boolean()
});

export type DeleteWhatsappSenderResponse = z.infer<typeof DeleteWhatsappSenderResponseSchema>;

/**
 * Get WhatsApp Sender Path Vars Schema
 */
export const GetWhatsappSenderPathVarsSchema = z.object({
  senderId: z.string()
});

export type GetWhatsappSenderPathVars = z.infer<typeof GetWhatsappSenderPathVarsSchema>;

/**
 * Get WhatsApp Sender Response Schema (from Twilio API)
 */
// Get uses same format as List (our entity, not Twilio format)
export const GetWhatsappSenderResponseSchema = WhatsappSenderSchema;

export type GetWhatsappSenderResponse = z.infer<typeof GetWhatsappSenderResponseSchema>;

/**
 * List WhatsApp Senders Response Schema
 */
export const ListWhatsappSendersResponseSchema = z.array(WhatsappSenderSchema);

export type ListWhatsappSendersResponse = z.infer<typeof ListWhatsappSendersResponseSchema>;

/**
 * Submit WhatsApp Sender Verification path vars
 *
 * Used when submitting the 6-digit OTP from Meta to complete phone verification.
 */
export const SubmitWhatsappSenderVerificationPathVarsSchema = z.object({
  connectionId: z.string().uuid(),
  senderId: z.string().min(1)
});

export type SubmitWhatsappSenderVerificationPathVars = z.infer<
  typeof SubmitWhatsappSenderVerificationPathVarsSchema
>;

/**
 * Submit WhatsApp Sender Verification body
 */
export const SubmitWhatsappSenderVerificationBodySchema = z.object({
  code: z
    .string()
    .length(6, "Code must be 6 digits")
    .regex(/^\d{6}$/, "Code must be 6 digits")
});

export type SubmitWhatsappSenderVerificationBody = z.infer<
  typeof SubmitWhatsappSenderVerificationBodySchema
>;

/**
 * Submit WhatsApp Sender Verification response (empty success)
 */
export const SubmitWhatsappSenderVerificationResponseSchema = z.object({});

export type SubmitWhatsappSenderVerificationResponse = z.infer<
  typeof SubmitWhatsappSenderVerificationResponseSchema
>;

// ============================================================================
// WHATSAPP TEMPLATES (Message Templates)
// ============================================================================

/**
 * Create WhatsApp Template Body Schema
 *
 * Follows Twilio Content API format; `types` is the Content API's `types`
 * object (see twilio-content-types.ts).
 */
export const CreateWhatsappTemplateBodySchema = z.object({
  connectionId: z.string().uuid(), // ApiKeyConnection ID (replaces subAccountId)
  friendlyName: z.string(),
  language: z.string(),
  types: z.custom<TwilioContentTypes>(),
  variables: z.record(z.string(), z.string()).optional()
});

export type CreateWhatsappTemplateBody = z.infer<typeof CreateWhatsappTemplateBodySchema>;

/**
 * WhatsApp Template Schema
 *
 * Represents a WhatsApp message template (from Twilio Content API).
 */
export const WhatsappTemplateSchema = z.object({
  id: z.string(),
  friendly_name: z.string().nullable(),
  language: z.string(),
  types: z.custom<TwilioContentTypes>(),
  variables: z.record(z.string(), z.string()).optional(), // Template variables (key → default value)
  approval_fetch: z.unknown().optional(),
  created_at: z.coerce.date().optional(),
  updated_at: z.coerce.date().optional()
});

export type WhatsappTemplate = z.infer<typeof WhatsappTemplateSchema>;

/**
 * Create WhatsApp Template Response Schema
 */
export const CreateWhatsappTemplateResponseSchema = WhatsappTemplateSchema;

export type CreateWhatsappTemplateResponse = z.infer<typeof CreateWhatsappTemplateResponseSchema>;

/**
 * List WhatsApp Templates Query Schema
 */
export const ListWhatsappTemplatesQuerySchema = z.object({
  connectionId: z.string().uuid().optional(),
  deploymentId: z.string().uuid().optional()
});

export type ListWhatsappTemplatesQuery = z.infer<typeof ListWhatsappTemplatesQuerySchema>;

/**
 * List WhatsApp Templates Response Schema
 */
export const ListWhatsappTemplatesResponseSchema = z.array(WhatsappTemplateSchema);

export type ListWhatsappTemplatesResponse = z.infer<typeof ListWhatsappTemplatesResponseSchema>;

/**
 * Delete WhatsApp Template Path Vars Schema
 */
export const DeleteWhatsappTemplatePathVarsSchema = z.object({
  templateId: z.string()
});

export type DeleteWhatsappTemplatePathVars = z.infer<typeof DeleteWhatsappTemplatePathVarsSchema>;

/**
 * Delete WhatsApp Template Response Schema
 */
export const DeleteWhatsappTemplateResponseSchema = z.object({
  deleted: z.boolean()
});

export type DeleteWhatsappTemplateResponse = z.infer<typeof DeleteWhatsappTemplateResponseSchema>;

// ============================================================================
// TEMPLATE APPROVALS (Meta Approval Process)
// ============================================================================

/**
 * WhatsApp Template Approval Categories
 *
 * Defined by Meta/WhatsApp for template approval process.
 * @see https://www.twilio.com/docs/whatsapp/tutorial/send-whatsapp-notification-messages-templates#whatsapp-notification-categories
 *
 * - UTILITY: Transactional messages (receipts, confirmations) - Faster approval
 * - MARKETING: Promotional messages - Stricter review
 * - AUTHENTICATION: OTP/2FA messages - Special approval process
 */
export const WHATSAPP_TEMPLATE_CATEGORIES = ["UTILITY", "MARKETING", "AUTHENTICATION"] as const;
export type WhatsappTemplateCategory = (typeof WHATSAPP_TEMPLATE_CATEGORIES)[number];

// =============================================================================
// Twilio Content API - Fetch an approval status request
// https://www.twilio.com/docs/content/content-api-resources#fetch-an-approval-status-request
// =============================================================================

/**
 * Twilio GET .../Content/{ContentSid}/ApprovalRequests response (whatsapp object).
 * Canonical shape from Twilio docs.
 */
export const TwilioWhatsappApprovalSchema = z.object({
  type: z.string(),
  name: z.string(),
  category: z.string(),
  content_type: z.string(),
  status: z.string(), // approved, rejected, pending, unsubmitted, etc.
  rejection_reason: z.string(),
  allow_category_change: z.boolean()
});

export type TwilioWhatsappApproval = z.infer<typeof TwilioWhatsappApprovalSchema>;

/**
 * Twilio "Fetch an approval status request" full response.
 * GET https://content.twilio.com/v1/Content/{ContentSid}/ApprovalRequests
 */
export const TwilioContentApprovalRequestResponseSchema = z.object({
  sid: z.string(),
  account_sid: z.string(),
  whatsapp: TwilioWhatsappApprovalSchema,
  url: z.string()
});

export type TwilioContentApprovalRequestResponse = z.infer<
  typeof TwilioContentApprovalRequestResponseSchema
>;

// =============================================================================
// Our API approval shape (contentAndApprovals list may use approvalRequests)
// =============================================================================

/**
 * Approval Request Schema (nested in contentAndApprovals response)
 */
export const ApprovalRequestSchema = z.object({
  allow_category_change: z.boolean(),
  category: z.string(),
  content_type: z.string(),
  flows: z.any(), // Required field (Twilio API always returns it, even if empty)
  name: z.string(),
  rejection_reason: z.string(),
  status: z.string() // unsubmitted, pending, approved, rejected
});

/**
 * Template Approval Schema (from Twilio contentAndApprovals.list())
 *
 * Matches Twilio API response structure
 */
export const TemplateApprovalSchema = z.object({
  sid: z.string(),
  approvalRequests: ApprovalRequestSchema
});

export type TemplateApproval = z.infer<typeof TemplateApprovalSchema>;

/**
 * List Template Approvals Query Schema
 */
export const ListTemplateApprovalsQuerySchema = z.object({
  connectionId: z.string().uuid()
});

export type ListTemplateApprovalsQuery = z.infer<typeof ListTemplateApprovalsQuerySchema>;

/**
 * List Template Approvals Response Schema
 */
export const ListTemplateApprovalsResponseSchema = z.array(TemplateApprovalSchema);

export type ListTemplateApprovalsResponse = z.infer<typeof ListTemplateApprovalsResponseSchema>;

/**
 * Submit Template Approval Body Schema
 *
 * Submit template for Meta WhatsApp approval.
 *
 * `name` is `.min(1)` because it is the WhatsApp template NAME Meta registers,
 * not a display label — it reaches `contents(sid).approvalCreate.create({name})`
 * verbatim, and a Content SID can be submitted to Meta exactly once. A blank
 * name was accepted here, refused by Meta, and rendered by
 * `whatsapp-templates.controller.ts` as HTTP 500 INTERNAL_ERROR with no route
 * forward. Refusing it in the contract turns that into a local 400 the client
 * can name; the client is expected to refuse it before the request is sent.
 */
export const SubmitTemplateApprovalBodySchema = z.object({
  connectionId: z.string().uuid(),
  templateId: z.string(),
  name: z.string().trim().min(1),
  category: z.enum(WHATSAPP_TEMPLATE_CATEGORIES)
});

export type SubmitTemplateApprovalBody = z.infer<typeof SubmitTemplateApprovalBodySchema>;

/**
 * Submit Template Approval Response Schema
 */
export const SubmitTemplateApprovalResponseSchema = z.object({
  sid: z.string(),
  status: z.string(),
  /** True when the template had already been submitted to Meta (informational, not an error). */
  alreadySubmitted: z.boolean()
});

export type SubmitTemplateApprovalResponse = z.infer<typeof SubmitTemplateApprovalResponseSchema>;

// =============================================================================
// Twilio API Response Schemas (for internal validation)
// =============================================================================

/**
 * Twilio Purchase Phone Number Response Schema
 *
 * Validates the response from Twilio's incoming phone numbers API.
 * Used internally by PurchaseWhatsappPhoneUseCase for type safety.
 */
export const TwilioPurchasePhoneResponseSchema = z.object({
  phoneNumber: z.string(),
  sid: z.string(),
  status: z.string(),
  friendlyName: z.string()
});

export type TwilioPurchasePhoneResponse = z.infer<typeof TwilioPurchasePhoneResponseSchema>;

// ============================================================================
// WHATSAPP SENDER PROFILE (Business Profile Management)
// ============================================================================

/**
 * WhatsApp Sender Profile Path Vars Schema
 */
export const GetWhatsappSenderProfilePathVarsSchema = z.object({
  senderId: z.string().min(1)
});

export type GetWhatsappSenderProfilePathVars = z.infer<
  typeof GetWhatsappSenderProfilePathVarsSchema
>;

/**
 * WhatsApp Sender Profile Response Schema
 *
 * Business profile information from Meta/Twilio
 */
export const WhatsappSenderProfileSchema = z.object({
  senderId: z.string(),
  displayName: z.string(),
  about: z.string().optional(),
  profilePhotoUrl: z.string().nullable().optional(),
  phoneNumber: z.string(),
  status: z.enum(["OFFLINE", "ONLINE"]).optional()
});

export type WhatsappSenderProfile = z.infer<typeof WhatsappSenderProfileSchema>;

/**
 * Get WhatsApp Sender Profile Response Schema
 */
export const GetWhatsappSenderProfileResponseSchema = WhatsappSenderProfileSchema;

export type GetWhatsappSenderProfileResponse = z.infer<
  typeof GetWhatsappSenderProfileResponseSchema
>;

/**
 * Update WhatsApp Sender Profile Path Vars Schema
 */
export const UpdateWhatsappSenderProfilePathVarsSchema = z.object({
  senderId: z.string().min(1)
});

export type UpdateWhatsappSenderProfilePathVars = z.infer<
  typeof UpdateWhatsappSenderProfilePathVarsSchema
>;

/**
 * Update WhatsApp Sender Profile Body Schema
 */
export const UpdateWhatsappSenderProfileBodySchema = z.object({
  displayName: z.string().trim().min(1).max(100).optional(),
  about: z.string().max(256).optional()
});

export type UpdateWhatsappSenderProfileBody = z.infer<typeof UpdateWhatsappSenderProfileBodySchema>;

/**
 * Update WhatsApp Sender Profile Response Schema
 */
export const UpdateWhatsappSenderProfileResponseSchema = z.object({
  updatedFields: z.array(z.string())
});

export type UpdateWhatsappSenderProfileResponse = z.infer<
  typeof UpdateWhatsappSenderProfileResponseSchema
>;

/**
 * Voice Preview Body Schema
 *
 * POST /api/deployments/voice-preview
 * Generates a short TTS sample for the given voice model (binary audio/mpeg response).
 */
export const VoicePreviewBodySchema = z.object({
  voice: z.string(),
  /** Voice provider — "openai" (default) or "gemini". */
  provider: z.enum(["openai", "gemini"]).optional(),
  /** BCP-47 language code for preview text (e.g. "en", "fr", "ja"). Defaults to "en". */
  language: z.string().optional()
});

export type VoicePreviewBody = z.infer<typeof VoicePreviewBodySchema>;
